/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-17 14:43:36
 *
 *******************************************************/

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Icon, TextField, Spinner } from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import InfiniteScroll from 'react-infinite-scroll-component';

import MainMenu from 'src/components/containers/main-menu';
import DidYouKnow from 'src/components/views/merchandising/did-you-know';
import TryNewVersion from 'src/components/containers/common/switchNewVersion/merchandising/try-new-version.jsx';
import ActionsGroupOnListing from 'src/components/views/merchandising/actions-group-on-listing';
import ListRules from 'src/components/views/merchandising/list-rules';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import SwitchVersionFeedbackModal from './switch-version-feedback-modal';

import { URL } from 'src/constants/links';
import { NUMBER_MERCHANDISING_PER_PAGE } from 'src/constants/merchandising';
import { prettyText } from 'src/helpers';
import { initCollectionsExpandStatus } from 'src/helpers/merchandising-helpers';
import * as merchandisingV2Actions from 'src/stores/actions/merchandising-v2-actions';
import * as merchandisingActions from 'src/stores/actions/merchandising-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as commonActions from 'actions/common-actions';
import { updateTenantApp } from 'actions/oauth-actions';
import TitleHelp from 'src/components/views/common/title-help';

import type { ComposedProps, State } from './index.type';

class Merchandising extends Component<ComposedProps, State> {
  state = {
    // Search string
    inputSearchText: '',

    skip: 0,
    limit: NUMBER_MERCHANDISING_PER_PAGE,

    allMerchandisings: [],
    hasMore: false,

    // Manage show all/part of collections for each rule
    collectionsExpandStatus: {},

    fromMerchandisingPageState: false,
    youtubeModal: false
  };

  async componentDidMount() {
    const { getMerchandisings } = this.props;
    const { skip, limit } = this.state;

    await getMerchandisings('', skip, limit);

    this.pushMerchandisingRulesToList();

    // Show typeform when go back from new/edit page
    const fromPage = _.get(this.props.location, 'state.fromPage', '');
    if (!_.isEmpty(fromPage)) {
      const { fromMerchandisingPageState } = this.state;
      this.setState({ fromMerchandisingPageState: !fromMerchandisingPageState });
    }

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const {
      saving,
      deleting,
      reordering,
      scheduleSyncIn,
      waitSyncAt,
      getStatusMigrate,
      gettingStatusMigrate,
      migrateStatus
    } = nextProps;
    const {
      saving: savingPrev,
      deleting: deletingPrev,
      reordering: reorderingPrev,
      getSyncProcessStatus,
      updateShowBanner
    } = this.props;

    if (gettingStatusMigrate === false && this.props.gettingStatusMigrate === true) {
      if (migrateStatus === 'running' || migrateStatus === 'undetermined') {
        clearTimeout(this.timeOutGetStatus);
        this.timeOutGetStatus = setTimeout(() => {
          getStatusMigrate();
        }, 3000);
      }
    }

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (deletingPrev && !deleting) || (reorderingPrev && !reordering)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }
      // Call sync process status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      loading,
      deleting,
      success,
      syncDuration,
      showScheduleSync,
      saveSuccess,
      selectedPlan,
      updateTenantAppLoading,
      enableMerchandisingV2,
      getStatusMigrate,
      migrateNewFeatureV2,
      migrateStatus,
      i18n,
    } = this.props;
    const {
      allMerchandisings,
      hasMore,
      skip,
      limit,
      inputSearchText,
      collectionsExpandStatus,
      fromMerchandisingPageState,
      youtubeModal
    } = this.state;

    // Check having merchandising
    const havingMerchandisingRules = !_.isEmpty(allMerchandisings);

    return (
      <div className="BC-Page BC-Page__Merchandising">
        <MainMenu menuId="menu-merchandising-merchandising" />
        <Page
          title={i18n.translate('AdminHTML.Merchandising.Common.ruleBasedMerchandising')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <ActionsGroupOnListing
                // PFSN-30966 Hide switch merchandising
                showButtonSwitchNew={false}
                onSwitchNew={this.onSwitchNew}
                loading={updateTenantAppLoading}
                havingMerchandisingRules={havingMerchandisingRules}
              />
            </div>
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={() => this.toggleModal('youtubeModal')}
            hrefUserManual={URL['Link_V2_Merchandising_RuleBaseMerchandising_ManualGuide']}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">

                {/* WARN: PFSN-30966 Hide switch merchandising - comment for hide - enable later*/}
                {/* Try new version */}
                {/* New feature0423: old customer flow - onboarding */}
                <TryNewVersion
                  migrateNewFeatureV2={migrateNewFeatureV2}
                  getStatusMigrate={getStatusMigrate}
                  migrateStatus={migrateStatus}
                  enableMerchandisingV2={enableMerchandisingV2}
                />
                {/* Did you know? */}
                <DidYouKnow />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />

                {/* Processing update banner */}
                {showScheduleSync ? (
                  <ProcessingUpdateBanner syncDuration={syncDuration} featureName="merchandising" />
                ) : (
                  ''
                )}
              </div>
            </Layout.Section>

            {/* List of merchandising */}
            <Layout.Section>
              <Card sectioned>
                <Card.Header>
                  <div className="search" onKeyDown={this.handleKeyDown} onKeyUp={this.handleKeyUp}>
                    <TextField
                      onChange={this.onSearchMerchandisings}
                      value={this.state.inputSearchText}
                      prefix={<Icon source={SearchMinor} color="base" />}
                      placeholder={i18n.translate(
                        'AdminHTML.Merchandising.Merchandising.textField_Placeholder'
                      )}
                    />
                  </div>
                </Card.Header>
                <Card.Section>
                  <div className={havingMerchandisingRules ? 'hasRules' : 'noRules'}>
                    <InfiniteScroll
                      dataLength={allMerchandisings.length}
                      next={this.loadMore}
                      hasMore={hasMore}
                      loader={
                        <div
                          key={'spinner'}
                          style={{ textAlign: 'center', padding: '9px 0 5px 0' }}>
                          <Spinner
                            accessibilityLabel={i18n.translate(
                              'AdminHTML.Merchandising.Merchandising.spinner_AccessibilityLabel'
                            )}
                            size="small"
                          />
                        </div>
                      }>
                      <ListRules
                        key={'list-rules'}
                        loading={loading}
                        rules={allMerchandisings}
                        collectionsExpandStatus={collectionsExpandStatus}
                        handleCollectionsExpand={this.handleCollectionsExpand}
                        handleChangeRuleStatus={this.handleChangeRuleStatus}
                        deleteRule={this.props.deleteMerchandising}
                        deleting={deleting}
                        success={success}
                        afterDeleteRule={this.afterDeleteRule}
                        reorderRules={this.reorderRules}
                      />
                    </InfiniteScroll>
                  </div>
                </Card.Section>
              </Card>

              {havingMerchandisingRules ? (
                <div className="pagination">
                  <div className="actionsGroup">
                    <ActionsGroupOnListing
                      q={inputSearchText}
                      skip={skip}
                      limit={limit}
                      havingMerchandisingRules={havingMerchandisingRules}
                    />
                  </div>
                </div>
              ) : (
                ''
              )}
            </Layout.Section>
          </Layout>
        </Page>

        <SwitchVersionFeedbackModal />

        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Merchandising_RuleBaseMerchandising_Video']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.Merchandising.Merchandising.youtubeModal_Title')}
        />
        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded
            type="merchandising"
            conditionShow={saveSuccess}
            conditionShowStateChange={fromMerchandisingPageState}
          />
        )}
      </div>
    );
  }

  // Push merchandisings to list everytime after reading from DB
  pushMerchandisingRulesToList = () => {
    let { skip, limit, allMerchandisings, collectionsExpandStatus, hasMore } = this.state;

    const { total, merchandisings = [] } = this.props;

    hasMore = skip + limit < total;

    // Init list of merchandising for first page
    if (skip === 0) {
      allMerchandisings = [];
    }

    // Merge new rules
    allMerchandisings = _.concat(allMerchandisings, merchandisings);

    // Init collections expand, show a part of collections by default
    let newCollectionsExpandStatus = initCollectionsExpandStatus(merchandisings);
    collectionsExpandStatus = _.assign(collectionsExpandStatus, newCollectionsExpandStatus);

    this.setState({ allMerchandisings, collectionsExpandStatus, hasMore });
  };

  // Handle view more/less for collections
  handleCollectionsExpand = ruleId => {
    const { collectionsExpandStatus } = this.state;
    collectionsExpandStatus[ruleId] = !collectionsExpandStatus[ruleId];

    this.setState({ collectionsExpandStatus });
  };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    const charList = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const key = event.key.toLowerCase();
    const keyCode = event.keyCode;

    if (charList.indexOf(key) === -1 && keyCode !== 8) {
      return;
    }

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.handleSearch();
    }, 500);
  };

  // On search text change
  onSearchMerchandisings = inputSearchText => {
    this.setState({ inputSearchText });
  };

  // Search redirects
  handleSearch = async () => {
    let inputSearchText = this.state.inputSearchText.toLowerCase().trim();
    inputSearchText = prettyText(inputSearchText);

    // Having search text, search merchandising rules
    if (inputSearchText) {
      // Set limit per page
      this.setState({
        skip: 0,
        limit: NUMBER_MERCHANDISING_PER_PAGE
      });

      // Init list before searching
      this.setState({ allMerchandisings: [] });

      await this.props.getMerchandisings(inputSearchText, 0, NUMBER_MERCHANDISING_PER_PAGE);
      this.pushMerchandisingRulesToList();
    } else {
      // Search text is empty, get all filter tree for page 1
      const skip = 0,
        limit = NUMBER_MERCHANDISING_PER_PAGE;

      // Set skip, limit for page 1
      this.setState({ skip, limit });

      // Get filter trees
      await this.props.getMerchandisings('', skip, limit);
      this.pushMerchandisingRulesToList();
    }
  };

  // Enable/disable merchandising rule
  handleChangeRuleStatus = async ruleId => {
    const { allMerchandisings } = this.state;
    const { updateMerchandising, getChecklist } = this.props;

    // Update rule status
    let rule = _.find(allMerchandisings, { id: ruleId });
    rule.status = rule.status === 'disabled' ? 'active' : 'disabled';

    // Change status
    await updateMerchandising(ruleId, rule, 'changeStatus');
    await getChecklist();
  };

  // Read more items
  loadMore = async page => {
    const { skip, limit, inputSearchText } = this.state;
    const { total } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page
    this.setState({ skip: nextSkip, limit });
    await this.props.getMerchandisings(
      inputSearchText || '',
      nextSkip,
      NUMBER_MERCHANDISING_PER_PAGE
    );
    this.pushMerchandisingRulesToList();
  };

  // Post process after delete rule
  afterDeleteRule = ruleId => {
    let { allMerchandisings } = this.state;

    allMerchandisings = _.reject(allMerchandisings, { id: ruleId });

    this.setState({ allMerchandisings });
  };

  // Reorder rules
  reorderRules = async (rules, startIndex, endIndex) => {
    let newOrders = [];
    let updatedRulesOrder = [];
    if (startIndex < endIndex) {
      updatedRulesOrder = rules.slice(startIndex, endIndex + 1);
    } else {
      updatedRulesOrder = rules.slice(endIndex, startIndex + 1);
    }

    newOrders = updatedRulesOrder.map(rule => {
      const { id, order } = rule;
      return { id, order };
    });

    // Update rule orders
    await this.props.reorderMerchandisings(newOrders);
    await this.props.getChecklist();

    // Save reorder rules to state
    this.setState({ allMerchandisings: rules });
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Action go to new version
  onSwitchNew = () => {
    const { updateTenantApp } = this.props;
    this.props.isOpenNewVersion(true);

    const body = { 'adminAppFeatures.merchandisingV2': true };
    updateTenantApp(body, {});
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  migrateNewFeatureV2: () => dispatch(commonActions.migrateNewFeatureV2(ownProps.i18n)),
  getStatusMigrate: () => dispatch(commonActions.getStatusMigrate(ownProps.i18n)),
  enableMerchandisingV2: enable =>
    dispatch(merchandisingV2Actions.enableMerchandisingV2(enable, ownProps.i18n)),
  getMerchandisings: (q, skip, limit) =>
    dispatch(merchandisingActions.getMerchandisings(q, skip, limit, ownProps.i18n)),
  updateMerchandising: (ruleId, merchandising, actionName) =>
    dispatch(
      merchandisingActions.updateMerchandising(ruleId, merchandising, actionName, ownProps.i18n)
    ),
  deleteMerchandising: ruleId =>
    dispatch(merchandisingActions.deleteMerchandising(ruleId, ownProps.i18n)),
  reorderMerchandisings: newOrders =>
    dispatch(merchandisingActions.reorderMerchandisings(newOrders, ownProps.i18n)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  updateTenantApp: (body, meta) => dispatch(updateTenantApp(body, meta, ownProps.i18n)),
  isOpenNewVersion: value => dispatch(merchandisingV2Actions.isOpenNewVersion(value))
});

const mapStateToProps = (state, props) => {
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  return {
    tenantId,
    merchandisings: _.get(state.merchandising, 'merchandisings', []),
    migrateStatus: _.get(state.common, 'status', null),
    gettingStatusMigrate: _.get(state.common, 'loading', null),
    total: _.get(state.merchandising, 'total', 0),
    loading: _.get(state.merchandising, 'loading', false),
    saving: _.get(state.merchandising, 'saving', false),
    deleting: _.get(state.merchandising, 'deleting', false),
    reordering: _.get(state.merchandising, 'reordering', false),
    success: _.get(state.merchandising, 'success', false),
    saveSuccess: _.get(state.merchandising, 'saveSuccess', false),
    scheduleSyncIn: _.get(state.merchandising, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.merchandising, 'waitSyncAt', 0),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    updateTenantAppLoading: _.get(state.oauth, 'loadingUpdate', false),
    introVersionModal: _.get(state.merchandisingV2, 'introVersionModal', false),
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(Merchandising)),
  'SyncMerchandise'
);
